import { supabase } from "@/lib/supabase";
import { isStoragePath, resolveAssetUrls } from "@/lib/mediaStorage";
import { isContentLive } from "@/lib/publishSchedule";
import {
  stories as mockStories,
  featuredStory as mockFeatured,
  type StoryPost,
  type StoryGalleryImage,
} from "@/mocks/stories";

/**
 * A story as shown on the public journal. It extends the existing `StoryPost`
 * shape so the journal UI components keep working unchanged, while adding a few
 * backend-specific fields (source table, id, featured flag, ISO date).
 */
export interface PublicStory extends StoryPost {
  id: string;
  source: "story" | "blog";
  featured: boolean;
  isoDate: string;
}

interface FarmerStoryRow {
  id: number;
  title: string;
  farmer_name: string | null;
  farm_name: string | null;
  village: string | null;
  category: string | null;
  excerpt: string | null;
  content: string | null;
  cover_image: string | null;
  read_time: string | null;
  featured: boolean | null;
  gallery: unknown;
  status: string;
  published_at: string | null;
  scheduled_for: string | null;
}

interface BlogPostRow {
  id: number;
  title: string;
  slug: string | null;
  category: string | null;
  excerpt: string | null;
  content: string | null;
  author: string | null;
  cover_image: string | null;
  tags: string[] | null;
  featured: boolean | null;
  gallery: unknown;
  status: string;
  published_at: string | null;
  scheduled_for: string | null;
}

const DEFAULT_AUTHOR = "Rythugaru Team";

/** Curated fallback covers used only when a record has no cover image set. */
const FALLBACK_COVERS = [
  "https://readdy.ai/api/search-image?query=Green%20paddy%20field%20in%20soft%20morning%20light%20in%20rural%20India%2C%20painterly%20stylized%20earthy%20green%20tones%2C%20calm%20atmosphere%2C%20high%20detail&width=1200&height=800&seq=journal-fallback-1&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Coconut%20grove%20with%20dappled%20warm%20sunlight%20in%20a%20South%20Indian%20village%2C%20painterly%20stylized%20green%20tones%2C%20calm%20atmosphere%2C%20high%20detail&width=1200&height=800&seq=journal-fallback-2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Indian%20farmer%20hands%20holding%20freshly%20harvested%20rice%20grains%2C%20warm%20golden%20light%2C%20painterly%20stylized%20earthy%20tones%2C%20calm%20atmosphere%2C%20high%20detail&width=1200&height=800&seq=journal-fallback-3&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Village%20canal%20and%20green%20fields%20in%20rural%20Andhra%20Pradesh%20at%20sunrise%2C%20painterly%20stylized%20green%20tones%2C%20calm%20atmosphere%2C%20high%20detail&width=1200&height=800&seq=journal-fallback-4&orientation=landscape",
];

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function hashString(input: string): number {
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function fallbackCover(seed: string): string {
  return FALLBACK_COVERS[hashString(seed) % FALLBACK_COVERS.length];
}

function formatDate(value: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function toIso(value: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString();
}

function normalizeReadTime(value: string | null, content: string | null): string {
  const trimmed = (value || "").trim();
  if (trimmed) return /read/i.test(trimmed) ? trimmed : `${trimmed} read`;
  const words = (content || "").trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

function splitParagraphs(content: string | null, fallback: string): string[] {
  const clean = (content || "").trim();
  if (!clean) return fallback ? [fallback] : [];
  const blocks = clean.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
  if (blocks.length > 1) return blocks;
  const lines = clean.split(/\n/).map((part) => part.trim()).filter(Boolean);
  return lines.length > 1 ? lines : [clean];
}

function deriveQuote(content: string | null, excerpt: string): string {
  const text = (content || excerpt || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  const match = text.match(/^[^.!?]*[.!?]/);
  const sentence = match ? match[0].trim() : text;
  if (sentence.length >= 24 && sentence.length <= 190) return sentence;
  return text.length <= 190 ? text : `${text.slice(0, 187).trim()}…`;
}

/**
 * Normalises a raw gallery value from the backend into the { src, alt } shape the
 * journal UI expects, tolerating either a `src` or `url` key on each entry.
 */
function normalizeGallery(value: unknown): StoryGalleryImage[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      const record = (entry || {}) as Record<string, unknown>;
      const src =
        typeof record.src === "string"
          ? (record.src as string)
          : typeof record.url === "string"
            ? (record.url as string)
            : "";
      const alt = typeof record.alt === "string" ? (record.alt as string) : "";
      return { src, alt };
    })
    .filter((image) => image.src);
}

function mapStoryRow(row: FarmerStoryRow): PublicStory {
  const content = row.content || "";
  const excerpt = row.excerpt || content;
  const dateValue = row.published_at || row.scheduled_for;
  return {
    id: `story-${row.id}`,
    source: "story",
    featured: Boolean(row.featured),
    isoDate: toIso(dateValue),
    slug: slugify(row.title),
    title: row.title,
    excerpt,
    category: row.category || "Farm Life",
    author: row.farmer_name || DEFAULT_AUTHOR,
    village: row.village || "Konaseema",
    date: formatDate(dateValue),
    readTime: normalizeReadTime(row.read_time, content),
    cover: row.cover_image || fallbackCover(`story-${row.id}`),
    quote: deriveQuote(content, excerpt),
    paragraphs: splitParagraphs(content, excerpt),
    gallery: normalizeGallery(row.gallery),
  };
}

function mapPostRow(row: BlogPostRow): PublicStory {
  const content = row.content || "";
  const excerpt = row.excerpt || content;
  const dateValue = row.published_at || row.scheduled_for;
  return {
    id: `blog-${row.id}`,
    source: "blog",
    featured: Boolean(row.featured),
    isoDate: toIso(dateValue),
    slug: row.slug || slugify(row.title),
    title: row.title,
    excerpt,
    category: row.category || "Journal",
    author: row.author || DEFAULT_AUTHOR,
    village: "Konaseema",
    date: formatDate(dateValue),
    readTime: normalizeReadTime(null, content),
    cover: row.cover_image || fallbackCover(`blog-${row.id}`),
    quote: deriveQuote(content, excerpt),
    paragraphs: splitParagraphs(content, excerpt),
    gallery: normalizeGallery(row.gallery),
  };
}

function mockJournal(): PublicStory[] {
  const all: StoryPost[] = [mockFeatured, ...mockStories];
  return all.map((story, index) => ({
    ...story,
    id: `mock-${index}`,
    source: "story",
    featured: story.slug === mockFeatured.slug,
    isoDate: toIso(story.date),
  }));
}

async function resolveAssets(list: PublicStory[]): Promise<PublicStory[]> {
  const pathSet = new Set<string>();
  list.forEach((story) => {
    if (isStoragePath(story.cover)) pathSet.add(story.cover);
    story.gallery.forEach((image) => {
      if (isStoragePath(image.src)) pathSet.add(image.src);
    });
  });
  if (pathSet.size === 0) return list;

  const map = await resolveAssetUrls([...pathSet]);
  return list.map((story) => ({
    ...story,
    cover: isStoragePath(story.cover) && map[story.cover] ? map[story.cover] : story.cover,
    gallery: story.gallery.map((image) =>
      isStoragePath(image.src) && map[image.src] ? { ...image, src: map[image.src] } : image,
    ),
  }));
}

/**
 * Loads every published farmer story and blog post, merges them into a single
 * journal, and sorts newest first. Falls back to the curated sample journal if
 * the backend is unreachable so the public site is never blank.
 */
export async function fetchPublishedStories(): Promise<PublicStory[]> {
  try {
    const [storyRes, postRes] = await Promise.all([
      supabase.from("farmer_stories").select("*").in("status", ["published", "scheduled"]),
      supabase.from("blog_posts").select("*").in("status", ["published", "scheduled"]),
    ]);

    if (storyRes.error && postRes.error) throw storyRes.error;

    const storyRows = ((storyRes.data as FarmerStoryRow[] | null) || []).filter((row) =>
      isContentLive(row.status, row.scheduled_for),
    );
    const postRows = ((postRes.data as BlogPostRow[] | null) || []).filter((row) =>
      isContentLive(row.status, row.scheduled_for),
    );

    const merged = [...storyRows.map(mapStoryRow), ...postRows.map(mapPostRow)];
    if (merged.length === 0) return mockJournal();

    const sorted = merged.sort((a, b) => (b.isoDate || "").localeCompare(a.isoDate || ""));
    return await resolveAssets(sorted);
  } catch {
    return mockJournal();
  }
}